import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../components/common/Icon';
import { PurposePills, TagPills } from '../../components/common/EventTags';
import { shortDate, wallClockTime } from '../../utils';

/* One event in a list — the admin's programme view and the employee's calendar both render this,
   so the two can never drift into showing different facts about the same event.

   ★ `date` and `end_date` arrive as WALL-CLOCK stamps already converted into the event's own
   timezone, so they are read literally (shortDate + wallClockTime) and never through Date()
   arithmetic, which would re-apply the browser's zone on top.

   Layout, and why: the NAME, its categories and the headcount share the top line — those are the
   three things you read to decide whether this row is the one you want, so splitting them over
   two lines or pushing the count to a vertically-centred column made the list look ragged. What
   the row asks you to DO (I'm coming / I'm not coming) is a full-width row of its own underneath,
   because a button that shares a line with a name gets squeezed on a phone.

     badges    inline after the name — counts, "you're coming"
     actions   far right of the top line — state pills, an admin button, the chevron
     children  a full-width row under the meta line — the answer buttons */
const CompanyEventRow = ({ event, onOpen, actions, badges, children, lang }) => {
  const { t } = useTranslation();
  const day = String(event.date || '').slice(8, 10);
  const month = shortDate(event.date, lang);
  const time = wallClockTime(event.date);
  const where = event.is_online
    ? t('onlineEvent')
    : (event.location_name || '');

  return (
    <div
      className={`ce-row ${event.is_past ? 'is-past' : ''}`}
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onClick={onOpen}
      onKeyDown={onOpen ? (e) => { if (e.key === 'Enter') onOpen(); } : undefined}
    >
      <span className="ce-date">
        <span className="ce-date__day">{day || '–'}</span>
        {/* The month out of the language-aware "4 Sep 2026", so the leaf follows the reader's
            locale without a second formatter. */}
        <span className="ce-date__mon">
          {(month || '').replace(/[\d\s]/g, '').slice(0, 4) || '—'}
        </span>
      </span>

      <span className="ce-row__body">
        <span className="ce-row__top">
          <span className="ce-row__name">{event.name}</span>
          <TagPills tags={event.tags} />
          {/* What it is FOR, right after what KIND it is — same line, dashed pills, so the two
              read as two things rather than one long list. */}
          <PurposePills items={event.purpose} />
          {badges}
        </span>
        <span className="ce-row__meta">
          {[month, time, where].filter(Boolean).join(' · ')}
        </span>
        {children && <span className="ce-row__buttons">{children}</span>}
      </span>

      <span className="ce-row__actions">
        {event.is_cancelled && (
          <span className="ce-pill ce-pill--cancelled">{t('eventCancelled')}</span>
        )}
        {!event.is_cancelled && !event.is_public && (
          <span className="ce-pill ce-pill--draft">{t('eventDraft')}</span>
        )}
        {/* Somebody else runs this one. Worth a pill and not a line of meta: it changes what
            every other fact on the row means — the venue is not ours, the programme is not ours,
            and "who is coming" is a headcount of our people at their event. */}
        {event.is_external_event && (
          <span className="ce-pill ce-pill--external">{t('externalEvent')}</span>
        )}
        {/* This day is a COMPETITION — it has a bracket, a round robin or pools behind it. A
            pill for the same reason "external" is one: it changes what the day is, and it
            changes what running it takes. Its own colour rather than a category chip, because nobody typed it
            — the event is one the moment somebody sets the competition up. */}
        {event.is_competition && (
          <span className="ce-pill ce-pill--competition">
            <Icon name="flag-checkered" /> {t('tournamentTitle')}
          </span>
        )}
        {/* An invite-only event that this viewer is NOT on the guest list for. It is in the
            list on purpose — the company knowing an event is happening is the point of the
            "listed" setting — but it carries a lock and no answer buttons, and the server has
            already withheld the headcount. */}
        {event.is_invite_only && event.i_am_invited === false && (
          <span className="ce-pill ce-pill--draft" title={t('inviteOnlyLockedHint')}>
            <Icon name="lock" /> {t('inviteOnlyLocked')}
          </span>
        )}
        {actions}
        {onOpen && <Icon name="angle-right" className="bravos-event__chev" />}
      </span>
    </div>
  );
};

export default CompanyEventRow;
